/* eslint-disable react/function-component-definition */
import React from 'react';

import FavoriteTwoTone from '@mui/icons-material/FavoriteTwoTone';
import { Card, CardContent, CardMedia, IconButton } from '@mui/material';
import classNames from 'classnames';

import { NavLink } from '@kamernet/core/Routing/NavLink';

import styles from './SearchResultCard.module.css';

export interface SearchResultCardProps {
  href?: string;
  onClick?: () => void;
  children: React.ReactNode;
}

const SearchResultCard = ({
  href = undefined,
  onClick = undefined,
  children,
}: SearchResultCardProps) => {
  return (
    <Card
      className={styles.root}
      component={NavLink.Native}
      underline="none"
      target="_blank"
      href={href}
      onClick={onClick}
    >
      {children}
    </Card>
  );
};

export interface SearchResultCardMediaProps {
  url: string | null;
  fallbackURL: string;
  alt: string;
  className?: string;
  onError: (event: React.SyntheticEvent<HTMLImageElement>) => void;
}

SearchResultCard.Image = function Image({
  url,
  fallbackURL,
  alt,
  className = undefined,
  onError,
}: SearchResultCardMediaProps) {
  return (
    <div>
      <CardMedia
        component="img"
        className={classNames(styles.media, className)}
        image={url || fallbackURL}
        alt={alt}
        loading="lazy"
        onError={onError}
      />
    </div>
  );
};

export interface SearchResultCardHeaderProps {
  image: React.ReactElement;
  labels?: React.ReactElement[];
  isFavorite: boolean;
  onFavorite: () => Promise<void>;
}

SearchResultCard.Header = function Header({
  image,
  labels = [],
  isFavorite,
  onFavorite,
}: SearchResultCardHeaderProps) {
  const [isFavoriteInternal, setIsFavoriteInternal] =
    React.useState(isFavorite);

  const handleFavoriteClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsFavoriteInternal(!isFavoriteInternal);

      onFavorite().catch(() => {
        // ignore
      });
    },
    [isFavoriteInternal, onFavorite],
  );

  return (
    <React.Fragment>
      {image}
      <div className={styles.header}>
        <div className={styles.labels}>{labels}</div>
        <IconButton
          aria-label="add to favorites"
          className={`${styles.favorite} ${
            isFavoriteInternal ? styles.filled : ''
          }`}
          onClick={handleFavoriteClick}
        >
          <FavoriteTwoTone fontSize="large" />
        </IconButton>
      </div>
    </React.Fragment>
  );
};

export interface SearchResultCardContentProps {
  children: React.ReactNode;
}

SearchResultCard.Content = function Content({
  children,
}: SearchResultCardContentProps) {
  return <CardContent className={styles.content}>{children}</CardContent>;
};

export interface SearchResultCardContentRowProps {
  children: React.ReactNode;
  className?: string;
}

SearchResultCard.ContentRow = function ContentRow({
  children,
  className = '',
}: SearchResultCardContentRowProps) {
  return (
    <div className={classNames(styles.contentRow, className)}>{children}</div>
  );
};

export { SearchResultCard };
